import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import {
  DiagramComponent,
  Inject,
  DataBinding,
  ComplexHierarchicalTree,
  LineDistribution,
  ConnectorBridging,
  ConnectionPointOrigin,
  ChildArrangement,
  DiagramConstraints,
} from '@syncfusion/ej2-react-diagrams';
import type { RoutingDto } from '../api/models/RoutingDto';
import type { OperationDto } from '../api/models/OperationDto';

interface RoutingVisualizationDialogProps {
  routing: RoutingDto | null;
  visible: boolean;
  onClose: () => void;
}

// Helper function to format duration in seconds to HH:MM:SS (optional tenths: 01:34:23.5)
const formatDuration = (seconds: number): string => {
  if (seconds == null || isNaN(seconds)) return '0:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const sRounded = Math.round(s * 10) / 10;
  const sStr = sRounded % 1 === 0
    ? String(Math.round(sRounded)).padStart(2, '0')
    : `${String(Math.floor(sRounded)).padStart(2, '0')}.${Math.round((sRounded - Math.floor(sRounded)) * 10)}`;
  return `${h}:${String(m).padStart(2, '0')}:${sStr}`;
};

// Format operation duration based on timeType
const formatOperationDuration = (operation: OperationDto): string => {
  const { timeType, durationSeconds, durationPerPieceSeconds, operationModes } = operation;

  switch (timeType) {
    case 'FIXED_DURATION':
      return durationSeconds
        ? `Dauer: ${formatDuration(durationSeconds)}`
        : 'Dauer: -';

    case 'QUANTITY_DEPENDENT':
      return durationPerPieceSeconds
        ? `${formatDuration(durationPerPieceSeconds)} / Stück`
        : 'Dauer: -';

    case 'RESOURCE_DEPENDENT':
      if (operationModes && operationModes.length > 0) {
        const durations = operationModes
          .map((mode) => mode.durationSeconds)
          .filter((d) => d !== undefined && d !== null) as number[];
        if (durations.length > 0) {
          const min = Math.min(...durations);
          const max = Math.max(...durations);
          return min === max
            ? `Dauer: ${formatDuration(min)} (ressourcenabh.)`
            : `Dauer: ${formatDuration(min)} - ${formatDuration(max)} (ressourcenabh.)`;
        }
      }
      return 'Dauer: ressourcenabhängig';

    case 'QUANTITY_AND_RESOURCE_DEPENDENT':
      if (operationModes && operationModes.length > 0) {
        const durations = operationModes
          .map((mode) => mode.durationPerPieceSeconds)
          .filter((d) => d !== undefined && d !== null) as number[];
        if (durations.length > 0) {
          const min = Math.min(...durations);
          const max = Math.max(...durations);
          return min === max
            ? `${formatDuration(min)} / Stück (ressourcenabh.)`
            : `${formatDuration(min)} - ${formatDuration(max)} / Stück (ressourcenabh.)`;
        }
      }
      return 'Dauer: ressourcenabhängig / Stück';

    default:
      return 'Dauer: -';
  }
};

// Format edge label from successor operation's min/max distance to predecessor (TIME chain is always END_TO_START)
const formatPredecessorDistanceLabel = (successorOperation: OperationDto, t: (key: string, opts?: { defaultValue?: string }) => string): string => {
  const parts: string[] = [];
  const translated = t('routing.form.relationTypes.endToStart', { defaultValue: 'Ende → Start' });
  parts.push(translated);

  if (successorOperation.minDistancePredecessor != null) {
    parts.push(`min: ${formatDuration(successorOperation.minDistancePredecessor)}`);
  }
  if (successorOperation.maxDistancePredecessor != null) {
    parts.push(`max: ${formatDuration(successorOperation.maxDistancePredecessor)}`);
  }

  return parts.join('\n');
};

// Create node from operation
const createNode = (operation: OperationDto): any => {
  const annotations: any[] = [
    {
      id: 'name',
      content: operation.name || 'Unbenannt',
      style: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#000',
      },
      offset: { x: 0.5, y: 0.2 },
    },
    {
      id: 'duration',
      content: formatOperationDuration(operation),
      style: {
        fontSize: 11,
        color: '#666',
      },
      offset: { x: 0.5, y: 0.5 },
    },
  ];

  if (operation.primaryResourceTypeName) {
    annotations.push({
      id: 'resource',
      content: `🔧 ${operation.primaryResourceTypeName}`,
      style: {
        fontSize: 10,
        fontStyle: 'italic',
        color: '#888',
      },
      offset: { x: 0.5, y: 0.8 },
    });
  }

  return {
    id: operation.uuid || `temp_${Date.now()}`,
    shape: {
      type: 'Basic',
      shape: 'Rectangle',
      cornerRadius: 5,
    },
    width: 180,
    height: 100,
    style: {
      fill: '#f8f9fa',
      strokeColor: '#333',
      strokeWidth: 2,
    },
    annotations,
  };
};

// Create connector between consecutive operations (predecessor → successor); label from successor's min/max distance to predecessor
const createConnector = (
  predecessor: OperationDto,
  successor: OperationDto,
  index: number,
  t: (key: string, opts?: { defaultValue?: string }) => string
): any => {
  const sourceId = predecessor.uuid || '';
  const targetId = successor.uuid || '';
  const label = formatPredecessorDistanceLabel(successor, t);

  return {
    id: `conn_${index}_${sourceId}_${targetId}`,
    sourceID: sourceId,
    targetID: targetId,
    type: 'Orthogonal',
    cornerRadius: 8,
    connectorSpacing: 16,
    style: {
      strokeColor: '#555',
      strokeWidth: 1.5,
    },
    annotations: [
      {
        id: 'label',
        content: label,
        style: {
          fontSize: 12,
          color: '#111',
          fontWeight: '600',
          fill: '#fff',
          strokeColor: '#555',
          strokeWidth: 1,
          textAlign: 'Center',
          textOverflow: 'Wrap',
        },
        offset: 0.5,
        constraints: 0,
      },
    ],
  };
};

const RoutingVisualizationDialog: React.FC<RoutingVisualizationDialogProps> = ({
  routing,
  visible,
  onClose,
}) => {
  const { t } = useTranslation(['common']);

  // Transform routing data to diagram format: linear chain from operations (by operationNumber), no use of operationRelationships
  const diagramData = useMemo(() => {
    if (!routing) {
      return { nodes: [], connectors: [] };
    }

    const operations = routing.operations || [];
    const sortedOps = [...operations].sort(
      (a, b) => (a.operationNumber ?? 0) - (b.operationNumber ?? 0)
    );

    const nodes = sortedOps.map(createNode);
    const connectors: any[] = [];
    for (let i = 0; i < sortedOps.length - 1; i++) {
      connectors.push(createConnector(sortedOps[i], sortedOps[i + 1], i, t));
    }

    return { nodes, connectors };
  }, [routing, t]);

  return (
    <>
      <style>
        {`
          .routing-visualization-dialog .e-dlg-content {
            padding: 0 !important;
            overflow: hidden !important;
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .routing-visualization-dialog .e-dlg-content > div {
            height: 100% !important;
            overflow: hidden !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .routing-visualization-diagram-container {
            flex: 1 !important;
            min-height: 0 !important;
            overflow: hidden !important;
            padding: 16px !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .routing-visualization-diagram-container .e-diagram {
            flex: 1 !important;
            min-height: 0 !important;
            overflow: auto !important;
            width: 100% !important;
          }
        `}
      </style>
      <DialogComponent
        width="90vw"
        height="95vh"
        visible={visible}
        close={onClose}
        showCloseIcon={true}
        isModal={true}
        cssClass="routing-visualization-dialog"
        header={routing ? `${t('routing.visualization.title', 'Visualisierung')}: ${routing.name}` : t('routing.visualization.title', 'Visualisierung')}
        footerTemplate={() => (
          <div className="flex justify-end space-x-2">
            <ButtonComponent type="button" onClick={onClose} cssClass="e-primary e-btn e-primary">
              {t('actions.close', 'Schließen')}
            </ButtonComponent>
          </div>
        )}
      >
        <div className="routing-visualization-diagram-container">
          {routing && diagramData.nodes.length > 0 ? (
            <DiagramComponent
              id="routing-diagram"
              width="100%"
              height="100%"
              nodes={diagramData.nodes}
              connectors={diagramData.connectors}
              layout={{
                type: 'ComplexHierarchicalTree',
                orientation: 'LeftToRight',
                horizontalSpacing: 180,
                verticalSpacing: 90,
                margin: { left: 60, top: 60, right: 60, bottom: 60 },
                connectionPointOrigin: ConnectionPointOrigin.DifferentPoint,
                arrangement: ChildArrangement.Linear,
                enableRouting: true,
              }}
              constraints={
                DiagramConstraints.Default |
                DiagramConstraints.Bridging
              }
              snapSettings={{
                constraints: 0,
              }}
              // Enable zoom and pan
              tool={0}
              // Enable selection
              selectedItems={{
                constraints: undefined,
              }}
            >
              <Inject
                services={[
                  DataBinding,
                  ComplexHierarchicalTree,
                  LineDistribution,
                  ConnectorBridging,
                ]}
              />
            </DiagramComponent>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">
                {routing
                  ? t('routing.visualization.noOperations', 'Keine Vorgänge vorhanden')
                  : t('routing.visualization.noRouting', 'Kein Arbeitsplan ausgewählt')}
              </p>
            </div>
          )}
        </div>
      </DialogComponent>
    </>
  );
};

export default RoutingVisualizationDialog;

